// Top-level Analyst (Ask Your Data) workbench.
//
// Full-bleed layout matching the approved sketch:
//   - top bar: session title + running credits + chat-logs toggle
//   - main:    conversation thread on a dot-grid ink canvas with a
//              floating composer (quick-action chips + lime send)
//   - right:   collapsible "Chat logs" rail (state persisted in
//              localStorage so a refresh keeps the choice)
//
// The panel owns its scroll (fills its parent, internal thread
// scroll) so the composer and rails stay put like a real workbench.
//
// Privacy / scope: the backend filters all reads/writes by
// (workspace_id, user_id) so this component only needs to ask via the
// usual headers (X-Workspace-Id from getLocalUser()).

import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Alert, Box, Button, IconButton, Stack, Typography, Tooltip, alpha,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  ViewSidebarOutlined as SidebarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  askStreamRequest, fetchSessionsRequest, loadSessionRequest,
  deleteSessionRequest, newSession, selectSession, appendPending,
  clearError,
} from '../../../redux/actions/signals/askData';
import { fetchRequest as signalListRequest } from '../../../redux/actions/signals/list';

import AskDataMessage from './AskDataMessage';
import AskDataInput from './AskDataInput';
import AskDataCostBadge from './AskDataCostBadge';
import { surfacesFor } from './uiTokens';


const LOGS_COLLAPSED_KEY = 'predictai.analyst.logsCollapsed';
// The setup page stashes the picked use case here so the chat's kickoff
// instruction can carry it (the model then skips the "what do you want
// to predict?" stage).
const SETUP_CONTEXT_KEY = 'predictai.setup.context';


const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};


// Within this many px of the bottom still counts as "pinned", so tiny
// wheel nudges don't cut the follow behaviour while a deliberate
// scroll-up does.
const PIN_THRESHOLD = 140;


// Thin scrollbar treatment used by the thread and the rail.
const thinScrollbar = (s) => ({
  scrollbarWidth: 'thin',
  scrollbarColor: `${s.border2} transparent`,
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-thumb': { bgcolor: s.border2, borderRadius: 4 },
  '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
});


// ---------------------------------------------------------------------------
// Right-hand "Chat logs" rail.
// ---------------------------------------------------------------------------
const ChatLogsRail = ({
  sessions, activeSessionId, onSelect, onNew, onClearAll, onDelete,
  overlay = false,
}) => (
  <Box
    sx={(t) => {
      const s = surfacesFor(t);
      return {
        width: 264, flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        borderLeft: `1px solid ${s.border}`,
        bgcolor: s.rail,
        p: '16px 12px 14px',
        // Phones: the rail floats over the thread instead of
        // squeezing it (the thread keeps the full width).
        ...(overlay ? {
          position: 'absolute',
          top: 0, right: 0, bottom: 0,
          width: 'min(280px, 84vw)',
          zIndex: 7,
          boxShadow: '-14px 0 34px rgba(0,0,0,0.28)',
        } : {}),
      };
    }}
  >
    <Stack
      direction="row" alignItems="center" justifyContent="space-between"
      sx={{ px: 0.75, pb: 1.25 }}
    >
      <Typography sx={(t) => ({ fontWeight: 700, fontSize: 13.5, color: surfacesFor(t).text })}>
        Chat logs
      </Typography>
      <Typography
        sx={(t) => {
          const s = surfacesFor(t);
          return {
            px: 1.25, py: 0.4,
            border: `1px solid ${s.border2}`,
            borderRadius: 999,
            fontSize: 11, fontWeight: 600,
            color: s.muted,
          };
        }}
      >
        {sessions.length} {sessions.length === 1 ? 'chat' : 'chats'}
      </Typography>
    </Stack>

    <Box
      role="button"
      tabIndex={0}
      onClick={onNew}
      onKeyDown={(e) => { if (e.key === 'Enter') onNew(); }}
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          py: '9px', mx: 0.25, mb: 1.25,
          borderRadius: '11px',
          border: `1px dashed ${s.border2}`,
          color: s.muted,
          fontSize: 12,
          cursor: 'pointer', userSelect: 'none',
          transition: 'all 120ms',
          '&:hover': {
            color: t.palette.mode === 'dark' ? t.brand?.lime : t.palette.primary.main,
            borderColor: t.brand?.teal || t.palette.primary.main,
          },
        };
      }}
    >
      <AddIcon sx={{ fontSize: 13 }} />
      New conversation
    </Box>

    <Box sx={(t) => ({ flex: 1, overflowY: 'auto', minHeight: 0, ...thinScrollbar(surfacesFor(t)) })}>
      {sessions.length === 0 ? (
        <Typography
          sx={(t) => ({ p: 2, display: 'block', textAlign: 'center', fontSize: 12, color: surfacesFor(t).faint })}
        >
          No conversations yet.
        </Typography>
      ) : (
        <Stack spacing="3px">
          {sessions.map((s) => {
            const active = s.session_id === activeSessionId;
            return (
              <Box
                key={s.session_id}
                onClick={() => onSelect(s.session_id)}
                sx={(t) => {
                  const u = surfacesFor(t);
                  return {
                    position: 'relative',
                    px: 1.5, py: 1,
                    borderRadius: '11px',
                    cursor: 'pointer',
                    bgcolor: active ? (t.brand?.lime || t.palette.primary.main) : 'transparent',
                    color: active ? (t.brand?.ink || '#0a0c0a') : u.muted,
                    fontWeight: active ? 600 : 400,
                    boxShadow: active ? '0 4px 18px rgba(192, 251, 110, 0.18)' : 'none',
                    transition: 'background-color 120ms',
                    '&:hover': {
                      bgcolor: active ? (t.brand?.lime || t.palette.primary.main) : u.panel,
                      color: active ? (t.brand?.ink || '#0a0c0a') : u.text,
                      '& .askdata-del': { opacity: 1 },
                    },
                  };
                }}
              >
                <Typography
                  noWrap
                  sx={{ fontSize: 12.5, fontWeight: 'inherit', pr: 3, color: 'inherit', lineHeight: 1.4 }}
                >
                  {s.title || 'Untitled'}
                </Typography>
                <Typography
                  noWrap
                  sx={{ display: 'block', fontSize: 10, opacity: 0.55, color: 'inherit', mt: '2px' }}
                >
                  {active ? 'active' : formatRelative(s.updated_at)} · {s.turn_count || 0} turns
                </Typography>
                <Tooltip title="Delete conversation">
                  <IconButton
                    className="askdata-del"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }}
                    sx={{
                      position: 'absolute', top: 6, right: 6,
                      p: 0.4, opacity: 0, transition: 'opacity 120ms',
                      color: 'inherit',
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>

    {sessions.length > 0 && (
      <Box
        role="button"
        tabIndex={0}
        onClick={onClearAll}
        onKeyDown={(e) => { if (e.key === 'Enter') onClearAll(); }}
        sx={(t) => {
          const s = surfacesFor(t);
          return {
            mt: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            py: '9px', borderRadius: 999,
            border: '1px solid rgba(255, 138, 128, 0.35)',
            color: s.danger,
            fontSize: 12, fontWeight: 600,
            cursor: 'pointer', userSelect: 'none',
            transition: 'background-color 120ms',
            '&:hover': { bgcolor: 'rgba(255, 138, 128, 0.08)' },
          };
        }}
      >
        <DeleteIcon sx={{ fontSize: 13 }} />
        Clear history
      </Box>
    )}
  </Box>
);


// Workspace-aware suggested prompts spanning what the chat can actually
// do: analyse, chart, import, forecast, simulate, alert, and run code.
// When the signal list is known we swap in chips grounded in the user's
// own data. The generic set must also fit occupied workspaces - the
// docked panel never fetches the signal list, so it always shows these.
const buildSuggestions = (signalKeys) => {
  if (!Array.isArray(signalKeys) || !signalKeys.length) {
    return [
      'Summarise this workspace in plain English',
      'Forecast my most active signal for the next 2 weeks',
      'Find anomalies and explain what drove them',
      'Set an alert that catches unusual movement',
      'I have a CSV — import it and set everything up',
      'Load a sample dataset to play with',
      'Clean my data with code: fill gaps, drop dead signals',
      'Give me a tour — what can you do?',
    ];
  }
  const [a, b] = signalKeys;
  return [
    `Forecast ${a} for the next 30 days`,
    `Create a goal that predicts ${a} and keeps improving`,
    ...(b ? [`What happens to ${a} if ${b} drops 20%?`] : []),
    `Alert me when ${a} moves abnormally`,
    'Summarise this workspace in plain English',
    'Find anomalies and explain what drove them',
    'Clean my data with code: fill gaps, drop dead signals',
    'Give me a tour — what can you do?',
  ].slice(0, 8);
};


const EmptyState = ({ suggestions, onPick, compact }) => (
  <Box sx={{ textAlign: 'center', pt: compact ? 5 : 14, pb: 6 }}>
    <Typography sx={(t) => ({ fontWeight: 600, fontSize: compact ? 14 : 15.5, color: surfacesFor(t).text })}>
      What should we do with your data?
    </Typography>
    <Typography sx={(t) => ({ mt: 0.5, fontSize: 13, color: surfacesFor(t).muted, maxWidth: 560, mx: 'auto' })}>
      Import data, chart and correlate signals, launch self-improving
      forecasts, test what-ifs, set alerts — or have it write and run code.
      Every step shows its receipts.
    </Typography>
    <Stack
      direction="row" spacing={1} sx={{ mt: 3.5, flexWrap: 'wrap', rowGap: 1 }}
      justifyContent="center" alignItems="center"
    >
      {(compact ? suggestions.slice(0, 6) : suggestions).map((txt) => (
        <Box
          key={txt}
          role="button"
          tabIndex={0}
          onClick={() => onPick(txt)}
          onKeyDown={(e) => { if (e.key === 'Enter') onPick(txt); }}
          sx={(theme) => {
            const s = surfacesFor(theme);
            return {
              px: 1.75, py: 0.8, borderRadius: 999,
              border: `1px solid ${s.border2}`,
              fontSize: 12.5,
              color: s.muted,
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'all 120ms',
              '&:hover': {
                borderColor: theme.brand?.teal || theme.palette.primary.main,
                color: theme.palette.mode === 'dark'
                  ? (theme.brand?.lime || theme.palette.primary.main)
                  : theme.palette.primary.main,
              },
            };
          }}
        >
          {txt}
        </Box>
      ))}
    </Stack>
  </Box>
);


const AskDataPanel = ({
  // 'full' (the /analyst page) or 'docked' (the shell sidecar). Docked
  // drops the chat-logs rail + floating chrome and tightens spacing;
  // the conversation itself is the same Redux state either way.
  variant = 'full',
  // state
  sessions, activeSessionId, messages, isLoading, error,
  totalCredits, lastUsage, userInfo, signalKeys,
  sessionsLoaded, signalListLoaded,
  // dispatch
  doAsk, doLoadSessions, doLoadSession, doDeleteSession,
  doNewSession, doSelectSession, doAppendPending, doClearError,
  doLoadSignals,
}) => {
  const docked = variant === 'docked';
  // Phones: the thread gets the whole width; the chat-logs rail
  // becomes an overlay drawer that starts closed.
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const [input, setInput] = useState('');
  // CSVs staged in the composer; uploaded when the question is sent.
  const [files, setFiles] = useState([]);
  // Collapsed state of the Chat logs rail survives refreshes (desktop);
  // phones always start collapsed regardless of the stored preference.
  const [logsCollapsed, setLogsCollapsed] = useState(() => {
    try {
      if (typeof window !== 'undefined'
          && window.matchMedia('(max-width:899.95px)').matches) {
        return true;
      }
      return localStorage.getItem(LOGS_COLLAPSED_KEY) === '1';
    } catch { return false; }
  });
  const navigate = useNavigate();
  const threadRef = useRef(null);   // the scrollable thread column
  const contentRef = useRef(null);  // inner column whose height grows
  // Stick-to-bottom: follow new content only while the user is at (or
  // near) the bottom. Scrolling up unpins; scrolling back down, sending
  // a question, or switching sessions re-pins.
  const pinnedRef = useRef(true);

  const toggleLogs = () => {
    setLogsCollapsed((prev) => {
      const next = !prev;
      // A phone toggle is per-visit only - it must not flip the
      // desktop preference.
      if (!isMobile) {
        try { localStorage.setItem(LOGS_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* private mode */ }
      }
      return next;
    });
  };

  const suggestions = React.useMemo(
    () => buildSuggestions(signalKeys),
    [signalKeys],
  );

  // Re-asks (suggestions, "Try again") send text only - staged files
  // stay in the composer. The composer's own submit passes them.
  const askQuestion = (q, withFiles = []) => {
    const text = (q || '').trim();
    if ((!text && !withFiles.length) || isLoading) return;
    const question = text
      || `Analyze the attached ${withFiles.length === 1 ? 'file' : 'files'}.`;
    doClearError();
    doAppendPending(
      question,
      withFiles.map((f) => ({ filename: f.name, size_bytes: f.size })),
    );
    doAsk({
      session_id: activeSessionId || undefined,
      question,
      files: withFiles,
    });
    setInput('');
    if (withFiles.length) setFiles([]);
    // Sending a question implies you want to watch the answer: re-pin
    // even if the user had scrolled up. The ResizeObserver scrolls as
    // soon as the pending bubble renders.
    pinnedRef.current = true;
  };

  useEffect(() => {
    doLoadSessions();
    // The signal list doubles as the workspace-emptiness check that
    // gates the setup kickoff (and feeds the tailored suggestions).
    // Only the FULL page fetches it: signal_list is shared with the
    // Signals page, and a dock-mounted fetch (page 1, default size)
    // would clobber whatever pagination that page just requested.
    if (!docked) doLoadSignals();
  }, [doLoadSessions, doLoadSignals, docked]);

  // ---------------------------------------------------------------
  // Chat-first workspace setup.
  //
  // A brand-new (empty) workspace auto-starts the guided setup: a
  // hidden kickoff turn makes the assistant greet the user and walk
  // them to their first goal via interactive blocks. If a setup
  // conversation already exists (reload mid-setup), resume it instead
  // of kicking off a duplicate.
  // ---------------------------------------------------------------
  const kickoffRef = useRef(false);
  useEffect(() => {
    // Setup only ever starts from the full /analyst page - the docked
    // sidecar must never fire a (credit-spending) kickoff turn from
    // some unrelated page just because the workspace is empty.
    if (docked) return;
    if (kickoffRef.current) return;
    if (!sessionsLoaded || !signalListLoaded) return;
    if (messages.length > 0 || isLoading) return;
    if (signalKeys.length > 0) return;           // workspace has data
    kickoffRef.current = true;
    if (sessions.length > 0) {
      // Empty workspace but the setup chat exists - pick it back up.
      doSelectSession(sessions[0].session_id);
      doLoadSession(sessions[0].session_id);
      return;
    }
    let raw = null;
    try {
      raw = localStorage.getItem(SETUP_CONTEXT_KEY);
      localStorage.removeItem(SETUP_CONTEXT_KEY);
    } catch (_e) { /* private mode */ }
    // New format: JSON {domain, target} from the setup wizard (target is
    // optional — it's a hint, not a commitment). Legacy sessions may
    // still carry a plain label string.
    let q = '';
    if (raw) {
      let ctx = null;
      try { ctx = JSON.parse(raw); } catch (_e) { ctx = null; }
      if (ctx && typeof ctx === 'object' && (ctx.target || ctx.domain)) {
        if (ctx.target) {
          q = ctx.domain
            ? `Help me set up this workspace. My domain is ${ctx.domain} and I want to predict: ${ctx.target}.`
            : `Help me set up this workspace. I want to predict: ${ctx.target}.`;
        } else {
          q = `Help me set up this workspace. My domain is ${ctx.domain}. I haven't picked an exact metric yet.`;
        }
      } else if (!ctx) {
        // Legacy plain-label context from before the two-step wizard.
        q = `Help me set up this workspace. I want to predict: ${raw}.`;
      }
    }
    doAppendPending(q, [], true);
    doAsk({ kickoff: true, ...(q ? { question: q } : {}) });
  }, [
    docked, sessionsLoaded, signalListLoaded, sessions, messages.length,
    isLoading, signalKeys.length, doAppendPending, doAsk,
    doSelectSession, doLoadSession,
  ]);

  const scrollToBottom = () => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Every scroll (user OR programmatic) re-derives pinned from the
  // actual position, so the state can never drift: our own bottom
  // scrolls keep it true, a user scrolling up flips it false, and
  // scrolling back down flips it true again.
  const handleThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedRef.current = distance < PIN_THRESHOLD;
  };

  // Follow ALL content growth while pinned - streamed text revealing,
  // charts/tables landing, skeletons appearing - not just new
  // messages. A ResizeObserver on the inner column catches every
  // layout change without polling.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (pinnedRef.current) scrollToBottom();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  // Switching (or first-loading) a session always lands at the latest
  // turn, pinned. `threadEmpty` flips when a loaded transcript arrives.
  const threadEmpty = messages.length === 0;
  useEffect(() => {
    pinnedRef.current = true;
    scrollToBottom();
  }, [activeSessionId, threadEmpty]);

  const handleSelect = (sid) => {
    if (sid === activeSessionId) return;
    doSelectSession(sid);
    doLoadSession(sid);
    // The phone overlay covers the thread - close it so the picked
    // conversation is immediately visible.
    if (isMobile) setLogsCollapsed(true);
  };

  const handleClearAll = () => {
    if (!sessions.length) return;
    const n = sessions.length;
    // eslint-disable-next-line no-alert
    const okay = window.confirm(
      `Delete all ${n} conversation${n === 1 ? '' : 's'}? This can't be undone.`,
    );
    if (!okay) return;
    sessions.forEach((s) => doDeleteSession(s.session_id));
  };

  // "Try again" on an assistant turn re-asks the user question that
  // produced it (a fresh turn in the same session).
  const retryQuestionFor = (idx) => {
    for (let j = idx - 1; j >= 0; j -= 1) {
      if (messages[j]?.role === 'user') return messages[j].content;
    }
    return null;
  };

  const handleSubmit = () => askQuestion(input, files);

  const userName = userInfo?.user?.name || userInfo?.name || 'You';
  const showEmpty = messages.length === 0 && !isLoading;
  const insufficient = error?.kind === 'insufficient_credits';

  return (
    <Box
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          flex: 1, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          bgcolor: s.ink,
        };
      }}
    >
      {/* Errors */}
      {insufficient && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0 }}
          action={
            <Button
              color="inherit" size="small"
              startIcon={<AccountBalanceIcon />}
              onClick={() => navigate('/settings/billing')}
            >
              Top up
            </Button>
          }
          onClose={doClearError}
        >
          {error.message || 'You\'re out of credits to ask a question.'}
        </Alert>
      )}
      {error && !insufficient && (
        <Alert severity="error" sx={{ borderRadius: 0 }} onClose={doClearError}>
          {error.message || 'Something went wrong.'}
        </Alert>
      )}

      {/* Body row: thread + chat-logs rail (overlaid on phones). */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
        {/* Thread column with the floating composer. */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Floating top-right cluster: running credits + logs toggle
              (the panel is header-less, so these live over the canvas).
              The docked sidecar has its own header - skip the chrome. */}
          {!docked && (
          <Stack
            direction="row" alignItems="center" spacing={1}
            sx={{ position: 'absolute', top: 14, right: 18, zIndex: 5 }}
          >
            <Box
              sx={(t) => ({
                borderRadius: 999,
                bgcolor: t.palette.mode === 'dark' ? 'rgba(10,12,10,0.75)' : 'rgba(246,245,240,0.8)',
                backdropFilter: 'blur(6px)',
              })}
            >
              <AskDataCostBadge totalCredits={totalCredits} lastUsage={lastUsage} />
            </Box>
            <Tooltip title={logsCollapsed ? 'Show chat logs' : 'Hide chat logs'}>
              <IconButton
                onClick={toggleLogs}
                size="small"
                aria-label={logsCollapsed ? 'Show chat logs' : 'Hide chat logs'}
                sx={(t) => {
                  const s = surfacesFor(t);
                  return {
                    width: 32, height: 32, borderRadius: '10px',
                    color: logsCollapsed ? (t.brand?.lime || t.palette.primary.main) : s.muted,
                    border: `1px solid ${s.border}`,
                    bgcolor: t.palette.mode === 'dark' ? 'rgba(10,12,10,0.75)' : 'rgba(246,245,240,0.8)',
                    backdropFilter: 'blur(6px)',
                    '&:hover': { bgcolor: s.panel, borderColor: s.border2 },
                  };
                }}
              >
                {/* Mirrored so the drawn panel matches our right-side rail. */}
                <SidebarIcon sx={{ fontSize: 17, transform: 'scaleX(-1)' }} />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
          <Box
            ref={threadRef}
            onScroll={handleThreadScroll}
            sx={(t) => {
              const s = surfacesFor(t);
              return {
                flex: 1, overflowY: 'auto',
                px: docked ? '14px' : { xs: '16px', md: '26px' },
                // Full mode clears the floating credits/toggle cluster.
                pt: docked ? 2 : 7.5,
                backgroundImage: `radial-gradient(circle, ${s.dot} 1px, transparent 1px)`,
                backgroundSize: '22px 22px',
                ...thinScrollbar(s),
              };
            }}
          >
            <Box ref={contentRef} sx={{ maxWidth: docked ? 'none' : 830, mx: 'auto' }}>
              {showEmpty ? (
                <EmptyState suggestions={suggestions} onPick={askQuestion} compact={docked} />
              ) : (
                messages.map((m, i) => (
                  <AskDataMessage
                    key={i}
                    message={m}
                    userName={userName}
                    onRetry={m.role === 'assistant' ? askQuestion : undefined}
                    retryQuestion={m.role === 'assistant' ? retryQuestionFor(i) : null}
                    // Interactive blocks (options, file drops, pickers,
                    // connector forms) are live only on the newest
                    // completed assistant turn; history renders static
                    // with the user's pick highlighted.
                    interactive={m.role === 'assistant' ? {
                      enabled: i === messages.length - 1 && !isLoading
                        && !m.streaming && !m.pending,
                      chosenText: messages[i + 1]?.role === 'user'
                        ? messages[i + 1].content : null,
                      onSend: askQuestion,
                    } : undefined}
                  />
                ))
              )}
              {/* Spacer so the last message clears the floating
                  composer when the thread is scrolled to the bottom. */}
              <Box sx={{ height: { xs: 220, md: 200 } }} />
            </Box>
          </Box>

          {/* Floating composer over the thread's bottom edge.
              The gradient is pointer-transparent so action cards in the
              last turn stay clickable; only the input itself captures. */}
          <Box
            sx={(t) => {
              const s = surfacesFor(t);
              return {
                position: 'absolute', left: 0, right: 0, bottom: 0,
                px: docked ? '14px' : { xs: '16px', md: '26px' },
                pt: 4, pb: docked ? '12px' : { xs: '12px', md: '18px' },
                background: `linear-gradient(180deg, transparent, ${s.ink} 46%)`,
                pointerEvents: 'none',
              };
            }}
          >
            <Box sx={{ maxWidth: docked ? 'none' : 800, mx: 'auto' }}>
              <Box sx={{ pointerEvents: 'auto' }}>
                <AskDataInput
                  value={input}
                  onChange={setInput}
                  onSubmit={handleSubmit}
                  isLoading={isLoading}
                  disabled={insufficient}
                  files={files}
                  onFilesChange={setFiles}
                />
              </Box>
              {!docked && (
                <Typography
                  sx={(t) => ({
                    display: 'block', textAlign: 'center', mt: 1,
                    fontSize: 10.5, color: surfacesFor(t).faint,
                    pointerEvents: 'none',
                  })}
                >
                  Grounded in your workspace — every query and action shown as a receipt.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {!docked && !logsCollapsed && (
          <ChatLogsRail
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelect={handleSelect}
            onNew={doNewSession}
            onClearAll={handleClearAll}
            onDelete={doDeleteSession}
            overlay={isMobile}
          />
        )}
      </Box>
    </Box>
  );
};


const mapStateToProps = (state) => ({
  sessions:        state.signal_ask_data?.sessions || [],
  activeSessionId: state.signal_ask_data?.activeSessionId || null,
  messages:        state.signal_ask_data?.messages || [],
  isLoading:       state.signal_ask_data?.isLoading || false,
  error:           state.signal_ask_data?.error || null,
  totalCredits:    state.signal_ask_data?.totalCredits || 0,
  lastUsage:       state.signal_ask_data?.lastUsage || null,
  sessionsLoaded:  Boolean(state.signal_ask_data?.sessionsLoaded),
  userInfo:        state.user_getUser?.response || null,
  // The signal list powers both the tailored suggestions and the
  // workspace-emptiness check that gates the setup kickoff.
  signalListLoaded: Boolean(state.signal_list?.response),
  signalKeys: (state.signal_list?.response?.signals || [])
    .map((p) => p?.key)
    .filter(Boolean),
});

const mapDispatchToProps = (dispatch) => ({
  // Streaming is the default path - the SSE saga handles all of
  // start/delta/tool/done/error so the user sees text as it's
  // produced and shimmer pills while the model is querying data.
  doAsk:           (payload) => dispatch(askStreamRequest(payload)),
  doLoadSessions:  () => dispatch(fetchSessionsRequest()),
  doLoadSession:   (sid) => dispatch(loadSessionRequest(sid)),
  doDeleteSession: (sid) => dispatch(deleteSessionRequest(sid)),
  doNewSession:    () => dispatch(newSession()),
  doSelectSession: (sid) => dispatch(selectSession(sid)),
  doAppendPending: (q, attachments, hidden) => dispatch(appendPending(q, attachments, hidden)),
  doClearError:    () => dispatch(clearError()),
  doLoadSignals:   () => dispatch(signalListRequest({ page: 1, rowsPerPage: 25, query: '' })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AskDataPanel);
